import React, {FunctionComponent, useEffect, useState} from "react";
import {AChartX} from "@atiautomacao/ati-ui-library";
import {useTheme} from "@mui/material";
import {addMinutes, format, isValid, parseISO} from "date-fns";
import {calculatePowerAxisMax, roundNextHundred, truncateNumber} from "../../../Utils/NumberUtil";
import { lighten } from "../../../Shared/Theme/DarkTheme";

interface PowerIrradiancePoint {
    dateTime: string;
    activePower: number | null;
    irradiance: number | null;
}

const adjustDataPoints = (dataPoints: PowerIrradiancePoint[]): PowerIrradiancePoint[] => {
    if (dataPoints.length === 0) {
        return dataPoints;
    }

    const firstDateTime = parseISO(dataPoints[0].dateTime);
    const lastDateTime = parseISO(dataPoints[dataPoints.length - 1].dateTime);

    const startTime = new Date(firstDateTime);
    startTime.setHours(4, 0, 0, 0); // 4:00
    const endTime = new Date(lastDateTime);
    endTime.setHours(20, 0, 0, 0); // 20:00

    let newData = [...dataPoints];
    while (firstDateTime > startTime) {
        const newDateTime = addMinutes(firstDateTime, -60);
        newData.unshift({
            dateTime: format(newDateTime, 'yyyy-MM-dd HH:mm'),
            activePower: null,
            irradiance: null,
        });
        firstDateTime.setTime(newDateTime.getTime());
    }

    while (lastDateTime < endTime) {
        const newDateTime = addMinutes(lastDateTime, 60);
        newData.push({
            dateTime: format(newDateTime, 'yyyy-MM-dd HH:mm'),
            activePower: null,
            irradiance: null,
        });
        lastDateTime.setTime(newDateTime.getTime());
    }

    return newData;
}

const PowerIrradianceMiniChart: FunctionComponent<{
    dataInstantPower: Array<any>;
    maxPower: number | null | undefined;
    powerStation: string;
}> = ({dataInstantPower, maxPower}) => {
    const [dataPoints, setDataPoints] = useState<PowerIrradiancePoint[]>([]);
    const theme = useTheme();

    useEffect(() => {
        if (dataInstantPower.length > 0) {
            setDataPoints(adjustDataPoints(dataInstantPower as PowerIrradiancePoint[]));
        } else {
            setDataPoints([]);
        }
    }, [dataInstantPower]);

    if (dataPoints.length === 0) {
        return null;
    }
    
    const powerAxisMax = calculatePowerAxisMax(maxPower);
    const maxIrradianceInData = dataPoints.reduce((max, point) => (point.irradiance != null && point.irradiance > max ? point.irradiance : max), 0);
    const irradianceAxisMax = maxIrradianceInData > 1200 ? roundNextHundred(maxIrradianceInData) : 1200;

    const xAxisData = dataPoints.map(point => {
        const date = parseISO(point.dateTime);
        return isValid(date) ? format(date, 'HH:mm') : point.dateTime;
    });

    const chartOption: any = {
        yAxis: [
            {
                min: 0,
                max: powerAxisMax,
                axisLabel: {show: false},
                splitLine: {show: false},
            },
            {
                min: 0,
                max: irradianceAxisMax,
                axisLabel: {show: false},
                splitLine: {show: false},
            }
        ],
        xAxis: {
            type: 'category',
            boundaryGap: true,
            data: xAxisData,
            axisLabel: {show: false},
            axisTick: {show: false},
            axisLine: {
                show: true,
                lineStyle: { color: 'rgba(0, 0, 0, 0.15)', width: 0.5 }
            },
        },
        series: [
            {
                name: 'Potência',
                type: 'line',
                smooth: 0.4,
                lineStyle: {
                    width: 1.9,
                },
                showSymbol: false,
                yAxisIndex: 0,
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,
                        colorStops: [
                            {offset: 0, color: 'rgba(45, 93, 231, 0.3)'},
                            {offset: 0.75, color: 'rgba(45, 93, 231, 0.06)'},
                            {offset: 1, color: 'rgba(45, 93, 231, 0)'},
                        ],
                    },
                },
                data: dataPoints.map(point => point.activePower != null ? truncateNumber(point.activePower, 2) : '-'),
            },
            {
                name: 'Irradiância',
                type: 'line',
                unity: 'W/m²',
                smooth: 0.4,
                lineStyle: {
                    width: 1.5,
                },
                showSymbol: false,
                yAxisIndex: 1,
                data: dataPoints.map(point => point.irradiance != null ? truncateNumber(point.irradiance, 2) : '-'),
            }
        ],
        legend: {
            show: false,
        },
        grid: {
            containLabel: true,
            top: 0,
            bottom: 10,

        },
        animationDuration: 2000,
        color: [
            theme.palette.mode === 'dark' ? lighten('rgba(45, 93, 231, 1)') : 'rgba(45, 93, 231, 1)' ,
            theme.palette.mode === 'dark' ? lighten('rgba(249, 168, 37, 1)') : 'rgba(249, 168, 37, 1)'
        ],
        tooltip: {
            trigger: 'axis',
            formatter: function (params: any) {
                let tooltipText = '';
                if (params.length > 0) {
                    tooltipText += `${params[0].axisValue}<br/>`;
                }
                params.forEach((param: any) => {
                    const unit = param.seriesName === 'Irradiância' ? 'W/m²' : 'kW';
                    tooltipText += `${param.marker} ${param.seriesName}: <b>${param.value} ${unit}</b><br/>`;
                });
                return tooltipText;
            },
        },
        toolbox: {
            trigger: 'axis',
            renderMode: 'richText',
            feature: {
                saveAsImage: {show: false}
            }
        }
    };

    return (
        <div
            style={{
                background: 'rgb(255 255 255 / 5%)',
                border: '1px solid rgba(45, 93, 231, 0.09)',
                borderRadius: '7px',
                height: '46px',
                width: '100%',
                display: "flex",
                justifyContent: "center",
                alignItems: 'center'
               
            }}
        >
            <AChartX
                height={40}
                width={'100%'}
                option={chartOption}
                theme={theme.palette.mode}
            />
        </div>

    );
}

export default PowerIrradianceMiniChart;
